import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sendRepairOrderRequestEmail } from '../../../redux/rnfizRedux';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { getUser } from '../../../redux/usersRedux';
import { loadBinsByWarehouseRequest } from '../../../redux/binsRedux';
import { summarizeFinalizations } from '../../features/RepairOrderDetails/RepairOrderDetails';

const RN_Fiz_4 = ({ props }) => {
  const user = useSelector((state) => getUser(state));
  const [bins, setBins] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [repairStatus, setRepairStatus] = useState(
    props.RN_Fiz_3 ? props.RN_Fiz_3.repairDecision : '',
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [repairDetails, setRepairDetails] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    const start = async () => {
      const bins = await dispatch(
        loadBinsByWarehouseRequest(user.users.warehouse),
      );
      setBins(bins.data);
    };
    start();
  }, [dispatch, user.users.warehouse]);

  const rows = useMemo(() => {
    if (!props.RepairOrders) return [];
    const binsLookup = bins.reduce((acc, bin) => {
      acc[bin.id] = bin.binName;
      return acc;
    }, {});
    return summarizeFinalizations(props.RepairOrders.segregation, binsLookup);
  }, [props.RepairOrders, bins]);

  const handleSubmit = async () => {
    const data = {
      emailGroup: {
        messageType: 'PZ04_SUSPECT',
        warehouseCode: props.warehouse,
      },
      subject: 'Prośba o zatwierdzenie zlecenia naprawy',
      template: 'repairOrderRequest',
      reportDetails: props,
    };
    const res = await dispatch(sendRepairOrderRequestEmail(data));
    if (res.status === 201) {
      setAlertMessage('Zlecenie naprawy zostało wysłane do zatwierdzenia');
      setAlertSeverity('success');
      setAlertOpen(true);
    } else {
      setAlertMessage('Wystąpił błąd podczas wysyłania zlecenia naprawy');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Underline,
    ],
    content: repairDetails,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML(); // Pobiera HTML z edytora
      setRepairDetails(html); // Ustawia stan z HTML
    },
  });

  const handleCategoryName = (category) => {
    switch (category) {
      case 'GOOD':
        return 'Wyrób zgodny';
      case 'DAMAGED':
        return 'Wyrób uszkodzony';
      case 'TO_REPAIR':
        return 'Wyrób do naprawy';
      default:
        return 'Nieznana kategoria';
    }
  };

  const handleStatusName = (status) => {
    switch (status) {
      case 'IN_PROGRESS':
        return 'W trakcie segregacji';
      case 'FINALIZED':
        return 'Segregacja zakończona';
      case 'CANCELED':
        return 'Segregacja anulowana';
      default:
        return 'Nieznany status';
    }
  };
  const handleDecision = (row) => {
    if (row.category === 'GOOD') {
      console.log('Wyrób zgodny - brak decyzji');
    }
    if (row.category === 'DAMAGED') {
      console.log('Wyrób uszkodzony - decyzja: złomowanie');
    }
  };
  return (
    <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert
          variant="filled"
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      <Typography
        variant="h6"
        component="h2"
        width="100%"
        gutterBottom
        fontWeight="bold"
        fontStyle="italic"
      >
        {t('rnModule.fiz_4.description')}
      </Typography>

      <Divider sx={{ width: '100%', mb: 2, bgcolor: '#C09A53' }} />
      {repairStatus ? null : (
        <Typography>{t('rnModule.fiz_4.noRepairDecision')}</Typography>
      )}
      {props.RepairOrders ? (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            fontWeight="bold"
            fontStyle="italic"
          >
            {t('rnModule.fiz_4.repairOrderDescription')}
          </Typography>
          <Divider sx={{ width: '100%', mb: 2, bgcolor: '#C09A53' }} />
          {editor && (
            <Stack direction="row" spacing={1} mb={1}>
              <IconButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                sx={{
                  color: editor.isActive('bold') ? 'primary.main' : 'inherit',
                  bgcolor: editor.isActive('bold')
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <FormatBoldIcon />
              </IconButton>
              <IconButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                sx={{
                  color: editor.isActive('italic') ? 'primary.main' : 'inherit',
                  bgcolor: editor.isActive('italic')
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <FormatItalicIcon />
              </IconButton>
              <IconButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                sx={{
                  color: editor.isActive('underline')
                    ? 'primary.main'
                    : 'inherit',
                  bgcolor: editor.isActive('underline')
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <FormatUnderlinedIcon />
              </IconButton>
              <IconButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                sx={{
                  color: editor.isActive('orderedList')
                    ? 'primary.main'
                    : 'inherit',
                  bgcolor: editor.isActive('orderedList')
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <FormatListNumberedIcon />
              </IconButton>
              <IconButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                sx={{
                  color: editor.isActive('bulletList')
                    ? 'primary.main'
                    : 'inherit',
                  bgcolor: editor.isActive('bulletList')
                    ? 'action.selected'
                    : 'transparent',
                }}
              >
                <FormatListBulletedIcon />
              </IconButton>
            </Stack>
          )}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              minHeight: 20,
              cursor: 'text', // Dodaj kursor
              '& .ProseMirror': {
                // Stylujemy wewnętrzny element edytora
                minHeight: '200px', // Dzięki temu będzie miejsce do klikania
              },
            }}
          >
            <EditorContent editor={editor} />
          </Paper>
          <Tooltip
            title={
              <Typography sx={{ fontSize: '15px' }}>
                {t('rnModule.fiz_4.tooltip.comment')}
              </Typography>
            }
            placement="top"
            slots={{ transition: Zoom }}
          >
            <TextField
              sx={{ width: '100%', mb: 2, mt: 2 }}
              label={t('rnModule.fiz_4.comment')}
              variant="outlined"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Tooltip>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            fontWeight="bold"
            fontStyle="italic"
          >
            {t('rnModule.fiz_4.repairOrderData')}
          </Typography>
          <Divider sx={{ width: '100%', mb: 2, bgcolor: '#C09A53' }} />
          {props.RepairOrders.status === 'OPEN' ? (
            <Typography>{t('rnModule.fiz_4.noActionRepairOrder')}</Typography>
          ) : (
            // <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            //   <Typography>
            //     {t('rnModule.fiz_4.repairOrderDescription')}:{' '}
            //     {props.RepairOrders.batch}
            //   </Typography>
            //   <Typography>Status: {props.RepairOrders.status}</Typography>
            // </Box>
            <TableContainer component={Paper}>
              <Table sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status wyrobu gotowego</TableCell>
                    <TableCell>Lokalizacja</TableCell>
                    <TableCell>Ilość</TableCell>
                    <TableCell>Decyzja</TableCell>
                    <TableCell>Akcja</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .sort((a, b) => b.category.localeCompare(a.category))
                    .map((row) => {
                      return (
                        <TableRow key={row.binId}>
                          <TableCell
                            sx={{
                              textTransform: 'capitalize',
                              color:
                                row.category === 'GOOD'
                                  ? 'green'
                                  : row.category === 'DAMAGED'
                                  ? 'red'
                                  : 'orange',
                              fontWeight: 'bold',
                              fontStyle: 'italic',
                              fontSize: '1.1rem',
                            }}
                          >
                            {handleCategoryName(row.category)}
                          </TableCell>
                          <TableCell>{row.binName}</TableCell>
                          <TableCell>{row.currentQty}</TableCell>
                          <TableCell>{row.decision}</TableCell>
                          <TableCell>
                            <Button onClick={() => handleDecision(row)}>
                              Test
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography>{t('rnModule.fiz_4.noRepairOrder')}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSubmit();
            }}
          >
            {t('rnModule.fiz_4.button.repairOrderRequest')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RN_Fiz_4;
